/**
 * ApprovalList — the shared approvals table that backs all 6
 * /attendance/approvals/* pages (Leave, Overtime, MissedClockIn,
 * BusinessTrip, OffSiteWork, ScheduleChange).
 *
 * Every approval page is a thin wrapper around this component:
 *
 *   <ApprovalList
 *     type="leave"
 *     title="Leave approvals"
 *     description={t('attendance:approvals.leaveDescription')}
 *     columns={[
 *       { key: 'leave_type', label: 'Leave Type', render: ... },
 *       { key: 'period',     label: 'Period',     render: ... },
 *       ...
 *     ]}
 *   />
 *
 * Why one component + 6 wrappers instead of 6 full pages?
 * All six read `GET /v1/attendance/approvals?type=<type>`, all six
 * display Worker / Status / Actions identically, all six have the
 * same bulk approve/reject/delete flow. The only per-type variance is
 * the 3–4 columns pulled out of `approval.data` (a JSONB dict). Column
 * declarations are ~20 lines per wrapper — the rest lives here.
 *
 * Data flow:
 *   1. fetchAll() on mount + on Refresh + after every mutation.
 *      Server-side `type` filter is passed in the query string so we
 *      never hand a wrapper rows it doesn't care about.
 *   2. Date-range + free-text filter are client-side (server supports
 *      date filtering on submitted_at but matches the mock UX better
 *      to keep all filtering consistent — and the volume per-tenant
 *      is small enough that client-side is fine).
 *   3. Per-row approve/reject hits PATCH /approvals/{id}. Bulk variants
 *      hit PATCH/DELETE /approvals/bulk and confirm via ConfirmDialog.
 *   4. "Add …" button is intentionally disabled for now — approvals are
 *      typically submitted by workers via the mobile app, not created
 *      from the ops UI. The button is kept visible to preserve the
 *      mock's affordance, and wired to a Snackbar hint.
 *
 * Loading states: spinner while first fetching; snackbar for mutation
 * outcomes; bulk-delete goes through ConfirmDialog (danger tone).
 */

import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid,
  IconButton, InputLabel, ListItemIcon, ListItemText, Menu, MenuItem,
  Divider, Paper, Select, Snackbar, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, CancelOutlined, CheckCircleOutlined, DeleteOutline,
  KeyboardArrowDown, RefreshOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import { useTranslation } from 'react-i18next';
import { PlaceSearchField } from '../../../components/location';
import { translateApiError } from '../../../i18n/translateApiError';
import type { Approval, ApprovalStatus, ApprovalType, ApprovalCreate } from '../../../api/attendance';
import type { WorkerResponse } from '../../../api/organization';
import { ConfirmDialog } from '../../../components/primitives';
// One implementation of the overtime span, shared with OvertimeApprovals. Two copies
// disagreed at midnight until 2026-08-25; see the module's header.
import { endsNextDay, overtimeHours } from './overtimeSpan';

import DateField from '../../../components/inputs/DateField';
import { fmtCalendarDate, fmtCalendarRange } from '../../../utils/datetime';
// ---------------------------------------------------------------------------
// Column declaration shape — each wrapper supplies an array of these.
// ---------------------------------------------------------------------------

export interface ApprovalColumn {
  /** Column header text. */
  label: string;
  /** Visual alignment; defaults to 'inherit'. */
  align?: 'left' | 'center' | 'right';
  /** Renderer — receives the approval row; usually reads `row.data[key]`. */
  render: (row: Approval) => ReactNode;
}

// ---------------------------------------------------------------------------
// Create-dialog field declaration — each wrapper declares its fields so
// ApprovalList can render the form generically and POST { worker_id,
// type, data: { ... } } to /v1/attendance/approvals.
// ---------------------------------------------------------------------------

export interface ApprovalFormField {
  /** Key written into the `data` JSONB. */
  key: string;
  label: string;
  type:
    | 'text'
    | 'textarea'
    | 'date'
    | 'time'
    | 'number'
    | 'select'
    /** Keyword address/place autocomplete over the Google Places API, degrading to a
     *  plain text box without a key. Added 2026-08-25: `business_trip.destination` and
     *  `off_site_work.location` were `text` HERE while mobile and the worker portal both
     *  offered the picker, so "record on behalf" was the one surface that made an admin
     *  type an address by hand (owner report). */
    | 'place'
    /** Backend-driven select. `optionsLoader` is invoked once when the
     *  dialog mounts; an unset list shows a CircularProgress. Used for
     *  Leave type (P1.7): hardcoded enums are fragile because admins
     *  can self-create LeaveTypes. */
    | 'dynamic_select'
    /** Inclusive day count derived from two date keys
     *  (`derivedFromDates`). Read-only by default; admins can flip
     *  the override toggle to type a manual value (e.g. half-days). */
    | 'derived_days'
    /** Decimal hour count derived from two HH:mm time keys
     *  (`derivedFromTimes`). Same override semantics as derived_days. */
    | 'derived_hours'
    /** Working-day count for the span in `derivedFromDates`, fetched from the
     *  SERVER for the worker the request is being recorded for.
     *
     *  Not computable in the browser: rest days come from the org's
     *  AttendanceRule and holidays from a per-worker calendar. `derived_days`
     *  above subtracts two dates, which is right for a DURATION and wrong for a
     *  working count — and doing it in the browser anyway is how this surface
     *  came to disagree with the two worker clients.
     *
     *  Always read-only and never submitted: `create_approval` recomputes it and
     *  discards anything sent, so an editable copy would be theatre. */
    | 'derived_working_days'
    /** The worker's CURRENT shift on the chosen date, fetched from
     *  `GET /v1/attendance/worker-shift?worker_id=&date=`.
     *
     *  Read-only and never submitted, for a stronger reason than
     *  `derived_working_days`: this was a free `dynamic_select` over EVERY timetable in
     *  the org, never resolved against the worker, so an admin could record "from
     *  Standard Day" for somebody on Early Shift and the approver read it as fact. The
     *  engine does not consume the value (`grep from_shift app/` is empty), so nothing
     *  else would ever have caught it — and since an approval now writes a temporary
     *  Schedule, the approver's decision changes how the day is measured.
     *
     *  NOT required, deliberately. "No schedule that day" is a real answer and a change
     *  from nothing to a shift is a legitimate request; the previous `required: true`
     *  BLOCKED it here while the same worker's own portal allowed it. */
    | 'derived_current_shift';
  required?: boolean;
  /** For type='select'. */
  options?: { value: string; label: string; snapshot?: string }[];
  /** For type='dynamic_select'. */
  optionsLoader?: () => Promise<{ value: string; label: string; snapshot?: string }[]>;
  /** For type='dynamic_select' — a SECOND key that receives the chosen option's
   *  `snapshot` (falling back to its label).
   *
   *  A shift change must submit two facts about one choice: the timetable ID the backend
   *  rosters the worker onto, and the NAME the approver reads. `timetables.name` has no
   *  unique constraint so the id cannot be inferred from the name, and resolving the name
   *  at read time would let a later rename rewrite what was asked for. Writing both from
   *  the same option is the only way the two cannot disagree. */
  snapshotKey?: string;
  /** For type='derived_days' — keys of the two date fields it watches. */
  derivedFromDates?: [string, string];
  /** For type='derived_hours' — keys of the two time fields it watches. */
  derivedFromTimes?: [string, string];
  /** For type='derived_working_days' — the key of a sibling half-day selector
   *  (`full` / `half_am` / `half_pm`). When the span is a SINGLE day and that key
   *  holds a half value, the preview shows 0.5, matching what
   *  `_apply_server_leave_days` will store. Without it the box would read 1 for a
   *  request the server records as a half. */
  halfDayFrom?: string;
  /** Grid span: 6 = half-width, 12 = full. Defaults 12. */
  span?: 6 | 12;
  /** Optional placeholder / helper. */
  placeholder?: string;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  /** Backend `type` filter passed to GET /attendance/approvals. Omit when
   *  `allTypes` is set (unified view fetches every worker type). */
  type?: ApprovalType;
  /**
   * Unified "All attendance requests" mode. Fetches every worker-submitted
   * type (the 6 QuTime types, excluding face-review approvals), shows a Type
   * column + a type filter, and renders a generic per-type Details summary
   * instead of the wrapper `columns`. The 6 per-type pages leave this unset.
   */
  allTypes?: boolean;
  /** Page H1 text. */
  title: string;
  /** Page subtitle text. */
  description: string;
  /** Label for the `Add …` affordance (e.g. 'Add Leave'). */
  /** DIALOG TITLE for the record-on-behalf form. It is NOT the button label: one
   *  string served both until 2026-08-25, so it had to read like a title and the
   *  button inherited "Record schedule change on behalf" — 32 characters in English,
   *  48 in French, and a dangling "au nom de" / "en nombre de" / "im Namen von" in
   *  every Romance and Germanic locale. The button now uses the shared, short
   *  `approvals.addButton`. */
  recordTitle: string;
  /** Type-specific columns inserted between Worker and Status. Ignored in
   *  `allTypes` mode (rows are mixed-type, so a generic summary is used). */
  columns?: ApprovalColumn[];
  /**
   * Field declarations for the "Add …" dialog. Each field writes into
   * data[key] on save. When omitted, the Add button toasts a deferral
   * message rather than opening the dialog — preserves the old behavior
   * for any page that can't yet be created from the UI.
   */
  createFields?: ApprovalFormField[];
  /** Optional free-text hint (defaults to 'Worker, Department'). */
  filterLabel?: string;
  /** Optional empty-state message when no rows match. */
  emptyHint?: string;
  /**
   * Optional context banner rendered at the top of the create dialog
   * once a worker is selected. Used by Leave (P1.7) to surface the
   * worker's leave_balance so an admin doesn't accidentally record an
   * over-balance request. Returning null hides the banner.
   *
   * Loaded lazily on worker change; the banner shows a small spinner
   * while loading.
   */
  topBannerLoader?: (workerId: string) => Promise<ReactNode | null>;
}

// ---------------------------------------------------------------------------
// Utility: quick-period shortcuts. Today = local date to keep the mock
// parity without hardcoding a calendar.
// ---------------------------------------------------------------------------

function isoDate(d: Date): string {
  // YYYY-MM-DD in local time (NOT toISOString which skews by UTC).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function monthStart(): string {
  const d = new Date();
  d.setDate(1);
  return isoDate(d);
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDate(d);
}

function todayIso(): string {
  return isoDate(new Date());
}

interface QuickPeriod {
  value: string;
  label: string;
  range: () => [string, string];
}

const QUICK_PERIODS: QuickPeriod[] = [
  {
    // The default, and the reason the range no longer needs an exception for pending
    // rows: with no bound, nothing outstanding can fall out of the window.
    value: 'all_time',
    label: 'All time',
    range: () => ['', ''],
  },
  {
    value: 'this_week',
    label: 'This Week',
    range: () => {
      const today = new Date();
      const dow = today.getDay(); // 0=Sun
      const offset = dow === 0 ? 6 : dow - 1; // back to Monday
      return [daysAgo(offset), todayIso()];
    },
  },
  {
    value: 'last_week',
    label: 'Last Week',
    range: () => {
      const today = new Date();
      const dow = today.getDay();
      const offset = dow === 0 ? 6 : dow - 1;
      return [daysAgo(offset + 7), daysAgo(offset + 1)];
    },
  },
  {
    value: 'this_month',
    label: 'This Month',
    range: () => [monthStart(), todayIso()],
  },
  {
    value: 'last_month',
    label: 'Last Month',
    range: () => {
      const d = new Date();
      d.setDate(1);
      const end = new Date(d);
      end.setDate(0); // last day of previous month
      d.setMonth(d.getMonth() - 1);
      return [isoDate(d), isoDate(end)];
    },
  },
];

// ---------------------------------------------------------------------------
// Visual helpers
// ---------------------------------------------------------------------------

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase() || '•';
}

function statusChipColor(s: string): 'warning' | 'success' | 'error' | 'default' {
  if (s === 'approved') return 'success';
  if (s === 'rejected') return 'error';
  if (s === 'pending') return 'warning';
  return 'default';
}

/** One label/value line in the detail dialog. */
const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ minWidth: 132, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Box>
);

function formatDateTime(
  value: string | null | undefined, locale: string,
): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  // The locale is passed, never defaulted: `toLocaleString()` with no argument
  // follows the BROWSER, so a zh-CN admin on an en-US machine would read one
  // date format in the table and another in this dialog.
  return parsed.toLocaleString(locale);
}

/** A payload key rendered for a reader, not a developer (svdy-rules §14).
 *
 *  Each request type carries its own `data` shape, so the dialog renders whatever
 *  is there. A translated label is used when the key is one we know; otherwise the
 *  raw key is de-snaked rather than shown as `off_site_reason`. */
/**
 * The order payload fields are shown in.
 *
 * Fixed, because `Object.entries()` returns whatever order the JSON happened to carry —
 * which is how the dialog came to show "End date" above "Start date". Anything not
 * listed follows, alphabetically, so a field a future request type adds still appears.
 */
const PAYLOAD_FIELD_ORDER = [
  'leave_type', 'reason', 'start_date', 'end_date', 'date', 'days', 'hours',
  'start_time', 'end_time', 'destination', 'location', 'note',
];

/**
 * Keys whose VALUE is an internal id and therefore must never be shown as-is.
 *
 * `lvt_01KS7442G0N6N78FM20HK4HE5M` tells an admin nothing — it is developer language
 * (svdy-rules §14) and it appeared on screen. Where a name can be resolved the name is
 * shown; where it cannot, the row is OMITTED, because an opaque token is worse than an
 * absent line: the reader cannot tell whether it is data or a bug.
 */
const ID_VALUED_FIELDS = new Set([
  'leave_type_id', 'worker_id', 'department_id', 'location_id',
  'schedule_id', 'timetable_id', 'reviewed_by',
]);

/** Derived or internal payload keys — never shown. Mobile twin: attendance-approvals `DETAIL_SKIP`. */
const HIDDEN_PAYLOAD_FIELDS = new Set([
  'duration_days', 'punch_id', 'time_entry_id', 'swap_with_worker_id',
]);

function payloadFieldLabel(
  key: string, t: (k: string, o?: Record<string, unknown>) => string,
): string {
  // `defaultValue` rather than comparing the result against the key.
  //
  // The comparison was the bug that put `approvals.payloadFields.leave_type_id` on
  // screen: i18next returns a missing key with the NAMESPACE STRIPPED, so
  // a namespaced miss comes back WITHOUT the namespace, so it never equals the string
  // it was compared to and the humanising fallback could not be reached.
  //
  // (Deliberately described in prose rather than shown as a key literal: localeParity's
  // scan does not strip comments, so an example key in a comment is reported as a
  // missing translation. Same self-reference trap as the `<StepUpModal` mount scan.)
  return t(`attendance:approvals.payloadFields.${key}`, {
    defaultValue: key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
  });
}

function formatPayloadValue(
  key: string, value: unknown, t: (k: string, o?: Record<string, unknown>) => string,
): string {
  if (value === null || value === undefined || value === '') return '—';
  // Was hardcoded 'Yes' / 'No'. Nine locales and two English words on screen.
  if (typeof value === 'boolean') return value ? t('common:yes') : t('common:no');
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  // `duration` is an ENUM on leave requests. It used to reach the screen as the raw wire
  // value, so an admin reading Chinese saw "full" in an otherwise translated row.
  if (key === 'duration' && typeof value === 'string') {
    return t(`attendance:approvals.payloadValues.duration.${value}`, { defaultValue: value });
  }
  // A bare calendar date in the reader's locale; any other string passes through unchanged.
  if (typeof value === 'string') return fmtCalendarDate(value);
  return String(value);
}

/**
 * The payload rows to show, in order, with internal ids resolved or dropped.
 *
 * Exported for its own test: the two rules here are the ones that were visibly wrong on
 * screen — an arbitrary field order, and `lvt_01KS7442G0N6N78FM20HK4HE5M` shown to a
 * human — and neither is observable from a snapshot of the happy path.
 */
export function visiblePayloadEntries(
  row: { data?: Record<string, unknown> | null },
  leaveTypeNameById: Map<string, string>,
): [string, unknown][] {
  const data = row.data ?? {};
  const entries = Object.entries(data)
    // Plumbing, not application content: `_`-prefixed bridge bookkeeping (`_process_request_id`, …)
    // and the internal ids/derived counts the mobile reviewer already skips (`DETAIL_SKIP`). They
    // reached the dialog as a de-snaked English label beside a raw token.
    .filter(([key]) => !key.startsWith('_') && !HIDDEN_PAYLOAD_FIELDS.has(key))
    .map(([key, value]): [string, unknown] | null => {
      if (!ID_VALUED_FIELDS.has(key)) return [key, value];
      // Resolvable ids become the name they stand for.
      if (key === 'leave_type_id') {
        const name = leaveTypeNameById.get(String(value));
        return name ? ['leave_type', name] : null;
      }
      // Everything else id-shaped is dropped rather than displayed. The reader cannot
      // act on a token, and cannot tell it from a rendering bug.
      return null;
    })
    .filter((e): e is [string, unknown] => e !== null);

  const rank = (key: string) => {
    const i = PAYLOAD_FIELD_ORDER.indexOf(key === 'leave_type' ? 'leave_type' : key);
    return i === -1 ? PAYLOAD_FIELD_ORDER.length : i;
  };
  return entries.sort(([a], [b]) => {
    const byRank = rank(a) - rank(b);
    // Alphabetical for anything the order list does not name, so a field a future
    // request type adds has a stable position rather than a random one.
    return byRank !== 0 ? byRank : a.localeCompare(b);
  });
}

function statusLabel(s: string, t: (key: string) => string): string {
  if (s === 'pending') return t('attendance:approvals.pending');
  if (s === 'approved') return t('attendance:approvals.approved');
  if (s === 'rejected') return t('attendance:approvals.rejected');
  // The worker withdrew it (`me/service.py` writes `cancelled`). Was capitalised English.
  if (s === 'cancelled') return t('attendance:approvals.cancelled');
  return s || '—';
}

// ---------------------------------------------------------------------------
// Unified ("All attendance requests") helpers — the 6 worker-submitted types
// (face-review approvals have their own surface and are excluded), a short
// type label, and a per-type payload summary for the generic Details column.
// ---------------------------------------------------------------------------

const WORKER_APPROVAL_TYPES: ApprovalType[] = [
  'leave', 'overtime', 'missed_clock_in', 'business_trip', 'off_site_work', 'schedule_change',
];

function typeLabel(type: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return t(`attendance:approvals.typeLabels.${type}`, { defaultValue: type });
}

function pick(data: unknown, key: string): string {
  if (data && typeof data === 'object' && key in (data as Record<string, unknown>)) {
    const v = (data as Record<string, unknown>)[key];
    return v == null ? '' : String(v);
  }
  return '';
}

/** Compact per-type summary of an approval's JSONB payload for the unified
 *  Details column (mirror of the mobile attendance-requests summarize()). */
function summarizeApproval(row: Approval): string {
  const d = row.data;
  const join = (parts: string[]) => parts.filter(Boolean).join('  ·  ');
  // Calendar dates in the reader's locale (zone-safe); a raw `2026-06-20` before.
  const range = (a: string, b: string) => fmtCalendarRange(a, b);
  const day = (key: string) => fmtCalendarDate(pick(d, key));
  switch (row.type) {
    case 'leave':
      return join([pick(d, 'leave_type'), range(pick(d, 'start_date'), pick(d, 'end_date'))]);
    case 'overtime':
      return join([day('date'), [pick(d, 'start_time'), pick(d, 'end_time')].filter(Boolean).join('–')]);
    case 'missed_clock_in':
      return join([day('date'), pick(d, 'time')]);
    case 'business_trip':
      return join([pick(d, 'destination'), range(pick(d, 'start_date'), pick(d, 'end_date'))]);
    case 'off_site_work':
      // The SPAN, with the legacy single `date` as a fallback (mobile summarize() reads the same).
      return join([pick(d, 'location'), range(pick(d, 'start_date'), pick(d, 'end_date')) || day('date')]);
    case 'schedule_change':
      return join([day('date'), [pick(d, 'from_shift'), pick(d, 'to_shift')].filter(Boolean).join(' → ')]);
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ApprovalList: React.FC<Props> = ({
  type,
  allTypes,
  title,
  description,
  recordTitle,
  columns,
  createFields,
  filterLabel,
  emptyHint,
  topBannerLoader,
}) => {
  const [rows, setRows] = useState<Approval[]>([]);
  const [workers, setWorkers] = useState<WorkerResponse[]>([]);
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const { t, i18n } = useTranslation();
  // Unbounded by default. An approval queue's job is "everything that still needs
  // attention, plus whatever history you ask for", and a default window is what made
  // outstanding work disappear at a month boundary. Empty strings mean no bound — the
  // predicate in `filtered` skips a comparison it has no value for.
  //
  // It costs nothing: the list request already fetches without dates, so this only
  // stops the client-side predicate from hiding rows.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [quickPeriod, setQuickPeriod] = useState('all_time');
  // The row a reader asked to inspect. Before 2026-08-06 there was NO way to see a
  // request's payload: the table showed a one-line summary in the Details column
  // and the row had `hover` with no handler, so the `reason` a worker typed when
  // applying — the only thing that justifies a decision — was unreachable, and a
  // decided request showed no trace of who decided it or why.
  const [detailRow, setDetailRow] = useState<Approval | null>(null);
  // `reviewed_by` is a worker id. The page already loads workers for the filter,
  // so resolve it — a raw `wrk_01K…` in a decision trail tells the reader nothing.
  const workerNameById = useMemo(
    () => new Map(workers.map((w) => [w.id, `${w.first_name ?? ''} ${w.last_name ?? ''}`.trim()])),
    [workers],
  );
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  /** Whose queue is this? Defaults to MINE, so the page keeps being an approval queue.
   *
   *  The list endpoint used to omit rows the caller could not review, which made a request an
   *  admin had just recorded on behalf of someone else unreachable from every surface — this is
   *  the only approvals-list endpoint (reported 2026-09-03). The backend now annotates each row
   *  with `awaiting_me` instead of dropping it, and this filter is what keeps the default view
   *  meaningful: a queue full of requests you cannot act on is noise, and it invites an admin to
   *  try and collect a 403. */
  const [scopeFilter, setScopeFilter] = useState<'mine' | 'all'>('mine');
  const [deptFilter, setDeptFilter] = useState<string>('all');
  /** Unified-mode type filter (ignored when a fixed `type` is set). */
  const [typeFilter, setTypeFilter] = useState<ApprovalType | 'all'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [toast, setToast] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState<ApprovalStatus | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Create-dialog state — only meaningful when `createFields` is supplied.
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createEmpId, setCreateEmpId] = useState('');
  const [createData, setCreateData] = useState<Record<string, string>>({});
  const [createError, setCreateError] = useState<string | null>(null);
  /** Per-field override flag for derived_days/derived_hours. When set
   *  the auto-compute is suppressed for that key and the admin can type
   *  a manual value (rare: half-day or unusual leave shapes). */
  const [createOverrides, setCreateOverrides] = useState<Record<string, boolean>>({});
  /** Loaded options for `dynamic_select` fields, keyed by field.key.
   *  null = still loading, [] = loaded empty (e.g. no leave types
   *  configured), array = ready. */
  // `snapshot` rides along so a `snapshotKey` field can write the chosen option's bare
  // name beside its id. Widened here as well as on `ApprovalFormField`: the state type is
  // declared separately, and `bun run test` cannot see the mismatch because CRA only
  // type-checks during `build` (svdy-ui §12.9).
  const [dynamicOptions, setDynamicOptions] = useState<
    Record<string, { value: string; label: string; snapshot?: string }[] | null>
  >({});
  /** Top-banner state: null = no worker selected, undefined = loaded
   *  empty, ReactNode = render. The 'loading' state is tracked
   *  separately. */
  const [topBanner, setTopBanner] = useState<ReactNode | null>(null);
  const [topBannerLoading, setTopBannerLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelected([]);
    try {
      const data = await attendanceApi.approvalsApi.list(allTypes ? undefined : { type });
      // Unified mode: keep only the 6 worker-submitted types (face-review
      // approvals have their own surface).
      setRows(allTypes ? data.filter((r) => WORKER_APPROVAL_TYPES.includes(r.type as ApprovalType)) : data);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.loadFailed'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [type, allTypes, t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /**
   * Leave-type names, fetched only once a detail dialog has been opened.
   *
   * Lazily, because most visits to this page never open a row, and the id → name map is
   * needed for exactly one payload field. Failure is non-fatal: the field is dropped
   * rather than shown as a raw id, which is the same outcome as an unresolvable id.
   */
  const [leaveTypeNameById, setLeaveTypeNameById] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!detailRow || leaveTypeNameById.size > 0) return;
    attendanceApi.leaveTypesApi
      .list()
      .then((types) => setLeaveTypeNameById(
        new Map((types as { id: string; name: string }[]).map((x) => [x.id, x.name])),
      ))
      .catch(() => { /* field is omitted; never a raw id */ });
  }, [detailRow, leaveTypeNameById.size]);

  // Load departments once for the Department filter. Non-fatal if it fails
  // — dropdown just stays empty.
  useEffect(() => {
    organizationApi.departmentsApi
      .list()
      .then((deps) => setDepartments(deps.map((d) => ({ id: d.id, name: d.name }))))
      .catch(() => setDepartments([]));
  }, []);

  // Workers needed only when the Add dialog is available; fetch lazily
  // the first time the dialog opens.
  useEffect(() => {
    if (createOpen && workers.length === 0) {
      organizationApi.workersApi
        .list('active')
        .then(setWorkers)
        .catch(() => setWorkers([]));
    }
  }, [createOpen, workers.length]);

  // --------------------------------------------------------------------
  // Create flow — POST /v1/attendance/approvals with { worker_id, type,
  // data }. Fields come from the wrapper; worker is the one constant.
  // --------------------------------------------------------------------

  const openCreate = () => {
    if (!createFields || createFields.length === 0) {
      setToast(t('attendance:approvals.workerSubmitsHint'));
      return;
    }
    setCreateEmpId('');
    setCreateData({});
    setCreateOverrides({});
    setCreateError(null);
    setTopBanner(null);
    setCreateOpen(true);
  };

  // Load dynamic_select options once when the dialog first opens.
  // Keep loaded state across opens; the wrapper's loader is expected
  // to be cheap (e.g. /v1/attendance/leave-types is small).
  useEffect(() => {
    if (!createOpen || !createFields) return;
    for (const f of createFields) {
      if (f.type !== 'dynamic_select') continue;
      if (dynamicOptions[f.key] !== undefined) continue;
      if (!f.optionsLoader) {
        setDynamicOptions((prev) => ({ ...prev, [f.key]: [] }));
        continue;
      }
      // Mark as loading (null) so the field renders a spinner.
      setDynamicOptions((prev) => ({ ...prev, [f.key]: null }));
      f.optionsLoader()
        .then((opts) =>
          setDynamicOptions((prev) => ({ ...prev, [f.key]: opts })),
        )
        .catch(() =>
          setDynamicOptions((prev) => ({ ...prev, [f.key]: [] })),
        );
    }
  }, [createOpen, createFields, dynamicOptions]);

  // Fetch the top banner whenever the selected worker changes inside
  // the dialog. Banners encode worker context (e.g. leave_balance) so
  // the admin can decide whether to record a request at all.
  useEffect(() => {
    if (!createOpen || !topBannerLoader) {
      setTopBanner(null);
      setTopBannerLoading(false);
      return;
    }
    if (!createEmpId) {
      setTopBanner(null);
      setTopBannerLoading(false);
      return;
    }
    let cancelled = false;
    setTopBannerLoading(true);
    topBannerLoader(createEmpId)
      .then((node) => {
        if (cancelled) return;
        setTopBanner(node);
      })
      .catch(() => {
        if (cancelled) return;
        setTopBanner(null);
      })
      .finally(() => {
        if (cancelled) return;
        setTopBannerLoading(false);
      });
    return () => { cancelled = true; };
  }, [createOpen, createEmpId, topBannerLoader]);

  // Working-day count for the chosen span, counted by the SERVER for the worker
  // the request is being recorded for. Display-only; submit is never gated on it,
  // so a network blip cannot block an otherwise complete request.
  const [workingDaysPreview, setWorkingDaysPreview] = useState<number | null>(null);
  const workingDaysField = useMemo(
    () => (createFields ?? []).find((f) => f.type === 'derived_working_days'),
    [createFields],
  );
  const wdStartKey = workingDaysField?.derivedFromDates?.[0];
  const wdEndKey = workingDaysField?.derivedFromDates?.[1];
  const wdStart = wdStartKey ? createData[wdStartKey] : undefined;
  const wdEnd = wdEndKey ? createData[wdEndKey] : undefined;

  /** Is a HALF day currently chosen? Derived from the declaration that already exists —
   *  `derived_working_days` names both its span keys and its `halfDayFrom` sibling, so the
   *  relationship does not need restating as a second config property. */
  const wdHalfKey = workingDaysField?.halfDayFrom;
  const wdHalfMarker = wdHalfKey ? createData[wdHalfKey] : undefined;
  /** Is the chosen branch a SINGLE day? `multi` is the only multi-day branch, so everything
   *  else — full, half_am, half_pm — collapses the span. This is the same four-branch question
   *  the two worker clients ask; the marker key is the duration select either way. */
  const wdIsSingleDayBranch = wdHalfKey
    ? (wdHalfMarker !== undefined && wdHalfMarker !== 'multi')
    : false;

  // A single-day branch names ONE day, so choosing one collapses the span. Reported 2026-09-03:
  // this form let an admin pick "Half day · morning" with 09/01 → 09/02 still in the date fields,
  // and the preview then showed 2 — correct for that span and wrong for the request, because the
  // combination should not have been reachable.
  //
  // Generalised from "is half" to "is not multi" once the `multi` branch was added, so `full` on
  // a single day behaves the same way rather than leaving a second reachable inconsistency.
  //
  // Writing it (not just displaying it) is the load-bearing half: without this the PAYLOAD would
  // still carry the two-day span, and `_apply_server_leave_days` would book 2 days against a
  // request the admin believes is one.
  useEffect(() => {
    if (!createOpen || !wdIsSingleDayBranch || !wdStartKey || !wdEndKey) return;
    if (wdStart && wdEnd !== wdStart) {
      setCreateData((d) => ({ ...d, [wdEndKey]: wdStart }));
    }
  }, [createOpen, wdIsSingleDayBranch, wdStart, wdEnd, wdStartKey, wdEndKey]);
  useEffect(() => {
    if (!createOpen || !workingDaysField || !createEmpId || !wdStart || !wdEnd) {
      setWorkingDaysPreview(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await attendanceApi.approvalsApi.businessDays(createEmpId, wdStart, wdEnd);
        if (!cancelled) setWorkingDaysPreview(data.business_days);
      } catch {
        if (!cancelled) setWorkingDaysPreview(null);
      }
    })();
    return () => { cancelled = true; };
  }, [createOpen, workingDaysField, createEmpId, wdStart, wdEnd]);

  // The worker's CURRENT shift on the chosen date, resolved by the SERVER for the worker
  // the request is being recorded for. Display-only and never submitted: `create_approval`
  // resolves it again and overwrites both keys, so an editable copy would be theatre —
  // which is exactly what the previous free picker was.
  const [currentShiftPreview, setCurrentShiftPreview] = useState<
    { name: string; clock_in: string | null; clock_out: string | null } | null
  >(null);
  const [currentShiftLoaded, setCurrentShiftLoaded] = useState(false);
  const currentShiftField = useMemo(
    () => (createFields ?? []).find((f) => f.type === 'derived_current_shift'),
    [createFields],
  );
  const csDateKey = currentShiftField?.derivedFromDates?.[0];
  const csDate = csDateKey ? createData[csDateKey] : undefined;
  useEffect(() => {
    if (!createOpen || !currentShiftField || !createEmpId || !csDate) {
      setCurrentShiftPreview(null);
      setCurrentShiftLoaded(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await attendanceApi.approvalsApi.workerShift(createEmpId, csDate);
        if (cancelled) return;
        setCurrentShiftPreview(data.current_shift ?? null);
        setCurrentShiftLoaded(true);
      } catch {
        if (!cancelled) { setCurrentShiftPreview(null); setCurrentShiftLoaded(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [createOpen, currentShiftField, createEmpId, csDate]);

  // Recompute derived_days / derived_hours whenever a watched key
  // changes. Suppress recompute when the admin has flagged the field
  // for manual override.
  useEffect(() => {
    if (!createOpen || !createFields) return;
    setCreateData((prev) => {
      let next: Record<string, string> | null = null;
      for (const f of createFields) {
        if (createOverrides[f.key]) continue;
        if (f.type === 'derived_days' && f.derivedFromDates) {
          const [aKey, bKey] = f.derivedFromDates;
          const a = prev[aKey];
          const b = prev[bKey];
          let computed = '';
          if (a && b) {
            const aT = Date.parse(a + 'T00:00:00Z');
            const bT = Date.parse(b + 'T00:00:00Z');
            if (Number.isFinite(aT) && Number.isFinite(bT) && bT >= aT) {
              computed = String(Math.round((bT - aT) / 86_400_000) + 1);
            }
          }
          if ((prev[f.key] ?? '') !== computed) {
            if (!next) next = { ...prev };
            next[f.key] = computed;
          }
        } else if (f.type === 'derived_hours' && f.derivedFromTimes) {
          const [aKey, bKey] = f.derivedFromTimes;
          // One implementation, shared with the list column. This branch used to
          // require `minutes > 0`, so a 22:00→02:00 night shift produced NOTHING
          // here and 4 hours once saved.
          const hours = overtimeHours(prev[aKey], prev[bKey]);
          const computed = hours === null ? '' : String(hours);
          if ((prev[f.key] ?? '') !== computed) {
            if (!next) next = { ...prev };
            next[f.key] = computed;
          }
        }
      }
      return next ?? prev;
    });
  }, [createOpen, createFields, createData, createOverrides]);

  const canSaveCreate = useMemo(() => {
    if (!createFields || !createEmpId) return false;
    for (const f of createFields) {
      // A hidden span end is not "missing" — the branch supplies it, and demanding it would
      // make Save unreachable with nothing on screen to fix.
      if (wdIsSingleDayBranch && f.key === wdEndKey) continue;
      if (f.required && !createData[f.key]?.trim()) return false;
    }
    // Cross-field validation: end_date >= start_date if both present.
    const startDate = createData.start_date;
    const endDate = createData.end_date;
    if (startDate && endDate && endDate < startDate) return false;
    return true;
  }, [createFields, createEmpId, createData, wdIsSingleDayBranch, wdEndKey]);

  const handleSaveCreate = async () => {
    if (!canSaveCreate || !createFields || !type) return;
    setCreateSaving(true);
    setCreateError(null);
    try {
      // Convert numeric fields from string → number so the backend
      // stores them as proper JSON numbers.
      const data: Record<string, unknown> = {};
      for (const f of createFields) {
        // Server-owned: `create_approval` recomputes it and discards anything
        // sent, so including it would only invite the reader to believe the
        // client chose it.
        if (f.type === 'derived_working_days') continue;
        // Same reason, and a stronger one: this used to be a free picker whose value
        // nothing validated and nothing consumed except the approver's eyes.
        if (f.type === 'derived_current_shift') continue;
        const v = createData[f.key];
        if (v == null || v === '') continue;
        const isNumeric =
          f.type === 'number' || f.type === 'derived_days' || f.type === 'derived_hours';
        data[f.key] = isNumeric ? Number(v) : v;
      }
      // `multi` is a UI-only branch. The wire vocabulary is duration ∈ full | half_am | half_pm,
      // so the branch is mapped here rather than taught to the server — and a single-day branch
      // has already mirrored the end date onto the start, so the span is right either way.
      if (data.duration === 'multi') delete data.duration;
      const payload: ApprovalCreate = {
        worker_id: createEmpId,
        type,
        data,
      };
      const created = await attendanceApi.approvalsApi.create(payload);
      // Say WHERE it went. The request the admin just recorded is approved by the worker's own
      // approver, so it is not in this queue's default view — and until 2026-09-03 the list
      // omitted it entirely, which read as "the create failed". A confirmation that does not
      // account for that is the same defect with a nicer toast (svdy-ui §19).
      setToast(
        created?.awaiting_me === false
          ? t('attendance:approvals.recordedNotInMyQueue')
          : t('attendance:approvals.requestRecorded'),
      );
      setCreateOpen(false);
      await fetchAll();
    } catch (e) {
      // Surface backend balance / validation errors inline rather
      // than bouncing through the snackbar — the dialog stays open
      // so the admin can adjust the days, switch leave type, or
      // cancel.
      setCreateError(
        e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'),
      );
    } finally {
      setCreateSaving(false);
    }
  };

  // ---------- client-side filtering ----------
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const deptById = new Map(departments.map((d) => [d.id, d.name]));
    const targetDeptName = deptFilter === 'all' ? null : deptById.get(deptFilter) ?? null;
    return rows.filter((r) => {
      // The range applies to EVERY row, with no exception.
      //
      // It used to exempt pending ones. The problem that produced that exemption was
      // real — a request filed on the 28th and still pending on the 5th fell outside
      // the "this month to date" DEFAULT and vanished from the only page that can act
      // on it, so the queue emptied itself at every month boundary and the dashboard
      // drill-through landed on "No requests in range" — but exempting a status was the
      // wrong shape of fix. It repaired the missing rows by breaking the control: on a
      // tenant whose queue is all pending, changing the dates did nothing at all, and
      // the owner reported that twice.
      //
      // The cause was the DEFAULT, so the default is what changed: it is now all time
      // (see `QUICK_PERIODS`). Nothing outstanding hides, the drill-through lands on a
      // page that contains the row, and the range means exactly what it says. One rule
      // and no sentence explaining an exception.
      const iso = (r.submitted_at ?? r.created_at ?? '').slice(0, 10);
      if (iso) {
        if (startDate && iso < startDate) return false;
        if (endDate && iso > endDate) return false;
      }
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      // `awaiting_me` is absent on older payloads; treat that as "mine" so a stale
      // client cannot hide rows it used to show.
      if (scopeFilter === 'mine' && r.awaiting_me === false) return false;
      if (allTypes && typeFilter !== 'all' && r.type !== typeFilter) return false;
      if (targetDeptName && (r.worker?.department ?? '') !== targetDeptName) return false;
      if (!q) return true;
      const hay = [
        r.worker?.name ?? '',
        r.worker?.department ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, startDate, endDate, filter, statusFilter, scopeFilter, deptFilter, departments, allTypes, typeFilter]);

  /**
   * The two halves of what the table is showing, and the reason this line exists.
   *
   * The date range exempts pending rows on purpose (see `filtered`). But a tenant with
   * a clean queue has ONLY pending rows, and then the range control visibly does
   * nothing — which is how the owner read this page on 2026-08-10, and they were right
   * to: a control that appears to have no effect is worse than no control.
   *
   * A static sentence under the page description already stated the exemption and was
   * not enough, because it is not where someone looks when they change a date. These
   * counts are: the second number moves when the range moves, which is the proof that
   * the control works, and the first number carries its own explanation.
   *
   * Derived from `filtered` rather than from `rows`, so the line can never disagree
   * with the table it sits above.
   */
  const pendingShown = useMemo(
    () => filtered.filter((r) => r.status === 'pending').length,
    [filtered],
  );
  const decidedShown = filtered.length - pendingShown;

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const allSelected =
    paginated.length > 0 && paginated.every((r) => selected.includes(r.id));
  const someSelected = selected.length > 0;
  // Bulk-actions dropdown menu state — collapses Approve/Reject/Delete
  // into a single button when rows are selected, instead of three
  // separate buttons that wrap on narrow viewports.
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<HTMLElement | null>(null);
  const closeBulkMenu = () => setBulkMenuAnchor(null);

  const toggleSelect = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const toggleAll = () =>
    setSelected(allSelected ? [] : paginated.map((r) => r.id));

  const handleQuickPeriod = (val: string) => {
    setQuickPeriod(val);
    const entry = QUICK_PERIODS.find((p) => p.value === val);
    if (entry) {
      const [s, e] = entry.range();
      setStartDate(s);
      setEndDate(e);
    }
    setPage(0);
  };

  const handleDateChange = (field: 'start' | 'end', value: string) => {
    if (field === 'start') setStartDate(value);
    else setEndDate(value);
    setQuickPeriod('custom');
    setPage(0);
  };

  // ---------- mutations ----------
  const handleSingleAction = async (id: string, status: ApprovalStatus) => {
    try {
      await attendanceApi.approvalsApi.update(id, { status });
      const statusLocalized = status === 'approved' ? t('attendance:approvals.approved')
        : status === 'rejected' ? t('attendance:approvals.rejected')
        : status === 'pending' ? t('attendance:approvals.pending')
        : status;
      setToast(t('attendance:approvals.toastRequestStatus', { status: statusLocalized }));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:approvals.toastActionFailed'));
    }
  };

  /**
   * Decide the row the dialog is showing.
   *
   * Delegates to `handleSingleAction` rather than repeating the call: one
   * implementation, two entry points. Closes on success only — a failed request leaves
   * the dialog open with its toast, because closing it would hide both the error and
   * the row the admin was working on.
   */
  const [deciding, setDeciding] = useState(false);
  const decideFromDetail = async (status: ApprovalStatus) => {
    if (!detailRow) return;
    setDeciding(true);
    const id = detailRow.id;
    try {
      await handleSingleAction(id, status);
      setDetailRow(null);
    } finally {
      setDeciding(false);
    }
  };

  const handleBulkConfirm = async () => {
    if (!bulkPending || selected.length === 0) return;
    try {
      await attendanceApi.approvalsApi.bulkUpdate({
        ids: selected,
        status: bulkPending,
      });
      setToast(
        bulkPending === 'approved'
          ? t('attendance:approvals.toastBulkApproved', { count: selected.length })
          : t('attendance:approvals.toastBulkRejected', { count: selected.length }),
      );
      setSelected([]);
      await fetchAll();
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('attendance:approvals.toastBulkFailed'),
      );
      throw e;
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selected.length === 0) return;
    try {
      await attendanceApi.approvalsApi.bulkDelete({ ids: selected });
      setToast(t('attendance:approvals.toastBulkDeleted', { count: selected.length }));
      setSelected([]);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
      throw e;
    }
  };

  // Columns actually rendered: the wrapper's per-type columns, OR — in
  // unified mode — a generic Type chip + per-type Details summary (rows are
  // mixed-type so the wrapper columns don't apply).
  const effectiveColumns: ApprovalColumn[] = useMemo(() => {
    if (!allTypes) return columns ?? [];
    return [
      {
        label: t('attendance:approvals.colType'),
        render: (r) => (
          <Chip
            label={typeLabel(r.type, t)}
            size="small"
            variant="outlined"
            sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }}
          />
        ),
      },
      {
        label: t('attendance:approvals.colDetails'),
        render: (r) => {
          const chain = (r as { approval_chain?: { level: string }[] | null }).approval_chain;
          const step = (r as { current_step?: number }).current_step ?? 0;
          return (
            <Box>
              <Typography variant="body2" color="text.secondary" noWrap>
                {summarizeApproval(r) || '—'}
              </Typography>
              {chain && chain.length > 1 && r.status === 'pending' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>
                  {t('attendance:approvals.stepProgress', {
                    n: step + 1, total: chain.length,
                    level: t(`attendance:approvals.level.${chain[step]?.level}`, { defaultValue: chain[step]?.level }),
                  })}
                </Typography>
              )}
            </Box>
          );
        },
      },
    ];
  }, [allTypes, columns, t]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}
          >
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          {/* Replaces a static caption that said the same thing in prose.
              Two sentences for one fact is the drift svdy-rules §10.5b describes, and
              the prose version had already failed its job: it stated the exemption and
              the page still read as broken, because a sentence under the title is not
              where a reader looks when they change a date.

              The dates are deliberately NOT repeated here — they are in the controls
              two inches away, and a second formatted copy is one more thing to keep in
              step for no gain. */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            <strong>{pendingShown}</strong>{' '}
            {t('attendance:approvals.stats.pendingAlways')}
            {' · '}
            <strong>{decidedShown}</strong>{' '}
            {t('attendance:approvals.stats.decidedInRange')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <DateField
            value={startDate}
            onChange={(e) => handleDateChange('start', e.target.value)}
            size="small"
            sx={{
              width: 135,
              '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ px: 0.25 }}>
            –
          </Typography>
          <DateField
            value={endDate}
            onChange={(e) => handleDateChange('end', e.target.value)}
            size="small"
            sx={{
              width: 135,
              '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' },
            }}
          />
          <Select
            value={quickPeriod}
            onChange={(e) => handleQuickPeriod(e.target.value)}
            size="small"
            sx={{
              minWidth: 110,
              height: 32,
              fontSize: '0.8rem',
              '& .MuiSelect-select': { py: 0.5, fontWeight: 600 },
            }}
          >
            {QUICK_PERIODS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t('common:periodOptions.' + o.value)}
              </MenuItem>
            ))}
            <MenuItem value="custom">{t("attendance:approvals.custom")}</MenuItem>
          </Select>
          <Tooltip title={t("attendance:approvals.refresh")}>
            <IconButton
              size="small"
              onClick={fetchAll}
              disabled={loading}
              sx={{
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              display: allTypes ? 'none' : undefined,
              // Never break the label across lines. Without this the control row's
              // `flexWrap` pushed the whole button onto a second line, where it landed
              // beside the page description and read as a collision (owner screenshot).
              whiteSpace: 'nowrap',
            }}
          >
            {t('attendance:approvals.addButton')}
          </Button>

          {/* Bulk actions — collapsed into a single dropdown so 3
              separate buttons (Approve / Reject / Delete) don't wrap
              on narrow viewports. Pattern: Linear / Asana / Stripe.
              Only visible when rows are selected. */}
          {someSelected && (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
                endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  bgcolor: '#0a0a0a',
                  color: '#fff',
                  '&:hover': { bgcolor: '#262626' },
                }}
              >
                {t('attendance:approvals.bulkActions', { count: selected.length })}
              </Button>
              <Menu
                anchorEl={bulkMenuAnchor}
                open={!!bulkMenuAnchor}
                onClose={closeBulkMenu}
                PaperProps={{
                  sx: {
                    minWidth: 200,
                    borderRadius: 1,
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  },
                }}
              >
                <MenuItem onClick={() => { setBulkPending('approved'); closeBulkMenu(); }}>
                  <ListItemIcon>
                    <CheckCircleOutlined fontSize="small" sx={{ color: 'success.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('attendance:approvals.bulkApprove', { count: selected.length })}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                  />
                </MenuItem>
                <MenuItem onClick={() => { setBulkPending('rejected'); closeBulkMenu(); }}>
                  <ListItemIcon>
                    <CancelOutlined fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('attendance:approvals.bulkReject', { count: selected.length })}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                  />
                </MenuItem>
                <MenuItem
                  onClick={() => { setBulkDeleteOpen(true); closeBulkMenu(); }}
                  sx={{
                    color: 'error.main',
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    mt: 0.5,
                  }}
                >
                  <ListItemIcon>
                    <DeleteOutline fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('attendance:approvals.bulkDelete', { count: selected.length })}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                  />
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      </Box>

      {/* Filter bar — FILTER_DESIGN_CONVENTION §3 (Who → What) +
          §5 chips. Location omitted: approvals are org-wide requests,
          not geo-scoped. */}
      <Box sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        mb: 1,
        gap: 1.5,
        flexWrap: 'wrap',
      }}>
        {/* Who — Department first in the filter bar */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t("attendance:approvals.departmentLabel")}</InputLabel>
          <Select value={deptFilter} label={t("attendance:approvals.departmentLabel")}
            onChange={(e) => { setDeptFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t("attendance:approvals.allDepartments")}</MenuItem>
            {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        {/* What — Type (unified mode only) */}
        {allTypes && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('attendance:approvals.typeLabel')}</InputLabel>
            <Select value={typeFilter} label={t('attendance:approvals.typeLabel')}
              onChange={(e) => { setTypeFilter(e.target.value as ApprovalType | 'all'); setPage(0); }}>
              <MenuItem value="all">{t('attendance:approvals.allTypesOption')}</MenuItem>
              {WORKER_APPROVAL_TYPES.map((ty) => (
                <MenuItem key={ty} value={ty}>{typeLabel(ty, t)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {/* Whose — before status, because it changes what the status counts are OF. */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t("attendance:approvals.scopeLabel")}</InputLabel>
          <Select value={scopeFilter} label={t("attendance:approvals.scopeLabel")}
            onChange={(e) => { setScopeFilter(e.target.value as typeof scopeFilter); setPage(0); }}>
            <MenuItem value="mine">{t("attendance:approvals.scopeMine")}</MenuItem>
            <MenuItem value="all">{t("attendance:approvals.scopeAll")}</MenuItem>
          </Select>
        </FormControl>
        {/* What */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t("attendance:approvals.statusLabel")}</InputLabel>
          <Select value={statusFilter} label={t("attendance:approvals.statusLabel")}
            onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setPage(0); }}>
            <MenuItem value="all">{t("attendance:approvals.allStatuses")}</MenuItem>
            <MenuItem value="pending">{t("attendance:approvals.pending")}</MenuItem>
            <MenuItem value="approved">{t("attendance:approvals.approved")}</MenuItem>
            <MenuItem value="rejected">{t("attendance:approvals.rejected")}</MenuItem>
          </Select>
        </FormControl>
        {/* Who — free-text last */}
        <TextField
          size="small"
          variant="outlined"
          label={filterLabel ?? t('attendance:approvals.filterLabel')}
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(0); }}
          sx={{ width: 300, maxWidth: '100%' }}
        />
        <Box sx={{ flex: 1 }} />
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }}
        />
      </Box>

      {/* Active filter chips — FILTER_DESIGN_CONVENTION §5. Date range
          lives in the Header row, so it doesn't appear as a chip. */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (deptFilter !== 'all') {
          const name = departments.find((d) => d.id === deptFilter)?.name ?? deptFilter;
          chips.push({ key: 'dept', label: `${t('attendance:approvals.departmentLabel')}: ${name}`,
            onClear: () => { setDeptFilter('all'); setPage(0); } });
        }
        if (statusFilter !== 'all') {
          const statusValue = statusFilter === 'pending' ? t('attendance:approvals.pending')
            : statusFilter === 'approved' ? t('attendance:approvals.approved')
            : statusFilter === 'rejected' ? t('attendance:approvals.rejected')
            : statusFilter;
          chips.push({ key: 'status',
            label: `${t('attendance:approvals.statusLabel')}: ${statusValue}`,
            onClear: () => { setStatusFilter('all'); setPage(0); } });
        }
        if (allTypes && typeFilter !== 'all') {
          chips.push({ key: 'type',
            label: `${t('attendance:approvals.typeLabel')}: ${typeLabel(typeFilter, t)}`,
            onClear: () => { setTypeFilter('all'); setPage(0); } });
        }
        if (filter.trim()) {
          chips.push({ key: 'search', label: `${t('common:actions.search')}: ${filter.trim()}`,
            onClear: () => { setFilter(''); setPage(0); } });
        }
        if (chips.length === 0) return null;
        return (
          <Box sx={{ width: '100%', display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {chips.map((c) => (
              <Chip key={c.key} label={c.label} size="small"
                onDelete={c.onClear} variant="outlined" color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }}
              />
            ))}
            <Button size="small"
              onClick={() => { setDeptFilter('all'); setStatusFilter('all'); setTypeFilter('all'); setFilter(''); setPage(0); }}
              sx={{
                textTransform: 'none', fontSize: '0.75rem',
                color: 'text.secondary', minWidth: 0, px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
              }}>
              {t('common:actions.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
      >
        <Table>
          <TableHead
            sx={{
              bgcolor: 'grey.100',
              '& .MuiTableCell-root': { fontWeight: 600 },
            }}
          >
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell>{t("attendance:approvals.colWorker")}</TableCell>
              {effectiveColumns.map((c, i) => (
                <TableCell key={i} align={c.align ?? 'left'}>
                  {c.label}
                </TableCell>
              ))}
              <TableCell align="center">{t("attendance:approvals.colStatus")}</TableCell>
              <TableCell align="center">{t("attendance:approvals.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3 + effectiveColumns.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3 + effectiveColumns.length}
                  align="center"
                  sx={{ py: 6, color: 'text.secondary' }}
                >
                  <Typography variant="body2">
                    {emptyHint ?? t('attendance:approvals.emptyDefault')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((r) => {
                const isPending = r.status === 'pending';
                return (
                  <TableRow key={r.id} hover selected={selected.includes(r.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.includes(r.id)}
                        onChange={() => toggleSelect(r.id)}
                      />
                    </TableCell>
                    {/* The click lives on the INFORMATIONAL cells, not on the
                        row: the checkbox cell and the actions cell mean different
                        things, and a row-level handler would fire for both
                        (svdy-ui §12.8 — clicking a geofence cell once opened a
                        selfie because the row owned the click). */}
                    <TableCell
                      onClick={() => setDetailRow(r)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={r.worker?.avatar ?? undefined}
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: '0.7rem',
                            bgcolor: 'primary.main',
                          }}
                        >
                          {initials(r.worker?.name ?? '•')}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.worker?.name ?? '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.worker?.department ?? '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {effectiveColumns.map((c, i) => (
                      <TableCell
                        key={i}
                        align={c.align ?? 'left'}
                        onClick={() => setDetailRow(r)}
                        sx={{ cursor: 'pointer' }}
                      >
                        {c.render(r)}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Chip
                        label={statusLabel(r.status, t)}
                        size="small"
                        color={statusChipColor(r.status)}
                        variant="outlined"
                        sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {isPending ? (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title={t("attendance:approvals.approve")}>
                            <IconButton
                              size="small"
                              onClick={() => handleSingleAction(r.id, 'approved')}
                              sx={{ color: 'success.main' }}
                            >
                              <CheckCircleOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("attendance:approvals.reject")}>
                            <IconButton
                              size="small"
                              onClick={() => handleSingleAction(r.id, 'rejected')}
                              sx={{ color: 'error.main' }}
                            >
                              <CancelOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bulk approve / reject confirmation */}
      <ConfirmDialog
        open={!!bulkPending}
        tone={bulkPending === 'approved' ? 'primary' : 'warning'}
        title={
          bulkPending === 'approved'
            ? t('attendance:approvals.bulkApproveTitle', { count: selected.length })
            : bulkPending === 'rejected'
              ? t('attendance:approvals.bulkRejectTitle', { count: selected.length })
              : ''
        }
        description={
          bulkPending === 'approved'
            ? t('attendance:approvals.bulkApproveDesc')
            : t('attendance:approvals.bulkRejectDesc')
        }
        confirmLabel={
          bulkPending === 'approved'
            ? t('attendance:approvals.approve')
            : t('attendance:approvals.reject')
        }
        confirmLoadingLabel={t('attendance:approvals.processing')}
        onConfirm={handleBulkConfirm}
        onClose={() => setBulkPending(null)}
      />

      {/* Bulk delete confirmation */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        tone="danger"
        title={t('attendance:approvals.bulkDeleteTitle', { count: selected.length })}
        description={t('attendance:approvals.bulkDeleteDesc')}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleBulkDeleteConfirm}
        onClose={() => setBulkDeleteOpen(false)}
      />

      {/* Create dialog — generic form driven by `createFields` prop.
          Chrome aligned to §13 (svdy-ui.md): borderRadius 2,
          subtitle2 700-weight title, dividers, DialogActions sx,
          info Alert above the form to make admin-side intent
          unmistakable. Layout puts Worker and content fields all
          inside the same Grid so the dialog reads as one form,
          not Stack-on-top + Grid-below. */}
      <Dialog
        open={createOpen}
        onClose={() => !createSaving && setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {recordTitle}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert
                severity="info"
                variant="outlined"
                sx={{ fontSize: '0.8rem' }}
              >
                {t('attendance:approvals.recordOnBehalfHint')}
              </Alert>
            </Grid>

            {/* Worker-context banner (e.g. leave balance) — wrappers
                inject by passing topBannerLoader. */}
            {topBannerLoading ? (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    {t('attendance:approvals.loadingWorkerContext')}
                  </Typography>
                </Box>
              </Grid>
            ) : topBanner ? (
              <Grid item xs={12}>{topBanner}</Grid>
            ) : null}

            {createError && (
              <Grid item xs={12}>
                <Alert
                  severity="error"
                  variant="outlined"
                  sx={{ fontSize: '0.8rem' }}
                  onClose={() => setCreateError(null)}
                >
                  {createError}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={workers}
                  getOptionLabel={(e) => `${e.first_name} ${e.last_name} (${e.worker_code})`}
                  value={workers.find((e) => e.id === createEmpId) ?? null}
                  onChange={(_, v) => setCreateEmpId(v?.id ?? '')}
                  renderInput={(params) => (
                    <TextField {...params} label={t("attendance:approvals.workerLabel")} required />
                  )}
                />
              </Grid>
              {(createFields ?? [])
                // The span END is not a question on a single-day branch — the branch answered
                // it. Hidden, not disabled: that is what stops "half a day across three dates"
                // from existing, rather than being offered and then dimmed. Same rule as the two
                // worker clients (attendanceRequestParity pins all three together).
                .filter((f) => !(wdIsSingleDayBranch && f.key === wdEndKey))
                .map((f) => {
                const value = createData[f.key] ?? '';
                const setValue = (v: string) =>
                  setCreateData((p) => ({ ...p, [f.key]: v }));
                const isDerived =
                  f.type === 'derived_days' || f.type === 'derived_hours';
                const overridden = !!createOverrides[f.key];

                if (f.type === 'derived_current_shift') {
                  // The SAME chrome as `derived_working_days` below — a read-only outlined
                  // TextField — because this dialog already had an answer for "a field the
                  // server owns" and the first version of this branch invented a second one:
                  // a caption label above a filled well, copied from the worker portal where
                  // every field looks like that.
                  //
                  // Dropped into a two-column MUI row it put a floating label beside a
                  // caption label, so the two controls in one row had different tops,
                  // different heights and different label conventions. Two treatments for one
                  // concept, in one dialog (§10.5b) — and §15's count of horizontal origins
                  // came out at two.
                  //
                  // The absence of a schedule is the PLACEHOLDER, not the value: grey and
                  // italic-weight reads as "there is nothing here", where a black
                  // "No schedule that day" would read as the name of a shift.
                  const label = currentShiftPreview
                    ? [
                      currentShiftPreview.name,
                      currentShiftPreview.clock_in && currentShiftPreview.clock_out
                        ? `(${currentShiftPreview.clock_in}\u2013${currentShiftPreview.clock_out})`
                        : null,
                    ].filter(Boolean).join(' ')
                    : '';
                  const csDone = !!createEmpId && !!csDate && currentShiftLoaded;
                  return (
                    <Grid item xs={12} sm={f.span ?? 12} key={f.key}>
                      <TextField
                        fullWidth
                        size="small"
                        label={f.label}
                        value={label}
                        placeholder={csDone
                          ? t('attendance:approvals.scheduleChange.noCurrentSchedule')
                          : t('attendance:approvals.scheduleChange.currentSchedulePending')}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  );
                }
                if (f.type === 'derived_working_days') {
                  // Read-only and never overridable: the number belongs to the
                  // server. An empty box (rather than a zero) while the span or
                  // the worker is still unchosen — a zero would read as a count.
                  //
                  // The half-day short-circuit mirrors `_apply_server_leave_days`:
                  // a SINGLE-day request marked morning/afternoon is 0.5 whatever
                  // the calendar says. Half of a multi-day span names no day, so
                  // the marker is ignored there — exactly as the server ignores it.
                  const halfMarker = f.halfDayFrom ? createData[f.halfDayFrom] : undefined;
                  const spanStart = f.derivedFromDates ? createData[f.derivedFromDates[0]] : undefined;
                  const spanEnd = f.derivedFromDates ? createData[f.derivedFromDates[1]] : undefined;
                  const isSingleDay = !!spanStart && spanStart === spanEnd;
                  const isHalf = isSingleDay
                    && (halfMarker === 'half_am' || halfMarker === 'half_pm');
                  const shown = isHalf ? 0.5 : workingDaysPreview;
                  // KEEPS the TextField chrome, and that is deliberate: `approvalDialogChrome`
                  // bans a caption-label-above-a-box here because that invented pattern gave
                  // this field a different top edge from the input beside it. Reverting to it
                  // would reintroduce an alignment defect somebody already fixed.
                  //
                  // What the owner reported (2026-09-03) is the OTHER half of the problem: a
                  // read-only outlined input still shows a caret, a hover outline and a focus
                  // ring, so it promises typing and refuses it. The prior decision's recorded
                  // reason is about ALIGNMENT, not about looking editable — so the box stays
                  // and the affordances go:
                  //
                  //   tabIndex -1        not reachable by keyboard, because there is nothing
                  //                      to do here
                  //   aria-readonly      says so to a screen reader
                  //   cursor: default    no I-beam
                  //   flat outline       no hover/focus emphasis
                  //
                  // Same metrics, no promise. `derived_days` beside it keeps the full input
                  // treatment because it really is overridable.
                  return (
                    <Grid item xs={12} sm={f.span ?? 12} key={f.key}>
                      <TextField
                        fullWidth
                        size="small"
                        label={f.label}
                        value={shown ?? ''}
                        placeholder="—"
                        InputProps={{ readOnly: true }}
                        inputProps={{ tabIndex: -1, 'aria-readonly': true }}
                        helperText={f.placeholder}
                        sx={{
                          '& .MuiInputBase-input': { cursor: 'default' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                          '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'divider',
                            borderWidth: 1,
                          },
                        }}
                      />
                    </Grid>
                  );
                }

                if (f.type === 'select') {
                  return (
                    <Grid item xs={12} sm={f.span ?? 12} key={f.key}>
                      <FormControl fullWidth size="small" required={f.required}>
                        <InputLabel>{f.label}</InputLabel>
                        <Select
                          value={value}
                          label={f.label}
                          onChange={(e) => setValue(e.target.value as string)}
                        >
                          {(f.options ?? []).map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                }

                if (f.type === 'dynamic_select') {
                  const opts = dynamicOptions[f.key];
                  const isLoading = opts === null || opts === undefined;
                  return (
                    <Grid item xs={12} sm={f.span ?? 12} key={f.key}>
                      <FormControl fullWidth size="small" required={f.required}>
                        <InputLabel>{f.label}</InputLabel>
                        <Select
                          value={value}
                          label={f.label}
                          disabled={isLoading}
                          onChange={(e) => {
                            const picked = e.target.value as string;
                            setValue(picked);
                            if (f.snapshotKey) {
                              const chosen = (opts ?? []).find((o) => o.value === picked);
                              const snapshotKey = f.snapshotKey;
                              setCreateData((prev) => ({
                                ...prev,
                                [snapshotKey]: chosen ? (chosen.snapshot ?? chosen.label) : '',
                              }));
                            }
                          }}
                          endAdornment={
                            isLoading ? (
                              <CircularProgress size={14} sx={{ mr: 3 }} />
                            ) : undefined
                          }
                        >
                          {(opts ?? []).map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                          {!isLoading && (opts ?? []).length === 0 && (
                            <MenuItem value="" disabled>
                              {t('attendance:approvals.noOptionsConfigured')}
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                }

                if (f.type === 'place') {
                  // The SAME component the worker portal and mobile use, so one control
                  // and one behaviour contract across all three surfaces.
                  return (
                    <Grid item xs={12} sm={f.span ?? 12} key={f.key}>
                      <PlaceSearchField
                        label={f.label}
                        required={f.required}
                        placeholder={f.placeholder}
                        value={String(createData[f.key] ?? '')}
                        onChange={(next) => setCreateData((prev) => ({ ...prev, [f.key]: next }))}
                      />
                    </Grid>
                  );
                }

                // text / textarea / date / time / number / derived_*
                const inputType = isDerived
                  ? 'number'
                  : f.type === 'textarea'
                    ? 'text'
                    : f.type;
                return (
                  <Grid item xs={12} sm={isDerived ? 12 : (f.span ?? 12)} key={f.key}>
                    <Stack spacing={0.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label={f.label}
                        type={inputType}
                        multiline={f.type === 'textarea'}
                        rows={f.type === 'textarea' ? 2 : undefined}
                        required={f.required}
                        placeholder={f.placeholder}
                        InputLabelProps={
                          f.type === 'date' || f.type === 'time'
                            ? { shrink: true }
                            : undefined
                        }
                        InputProps={
                          isDerived && !overridden
                            ? { readOnly: true }
                            : undefined
                        }
                        helperText={
                          isDerived && !overridden
                            ? f.type === 'derived_days'
                              ? t('attendance:approvals.helperAutoDays')
                              // A wrapped span must SAY it wrapped. "4 hours" against
                              // a 22:00–02:00 entry is correct and unreadable unless
                              // the next day is stated.
                              : f.type === 'derived_hours' && f.derivedFromTimes
                                && endsNextDay(
                                  createData[f.derivedFromTimes[0]],
                                  createData[f.derivedFromTimes[1]],
                                )
                                ? t('attendance:approvals.overtime.endsNextDay')
                                : t('attendance:approvals.helperAutoHours')
                            : isDerived && overridden
                              ? t('attendance:approvals.helperManualOverride')
                              : undefined
                        }
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                      />
                      {isDerived && (
                        <Button
                          size="small"
                          onClick={() =>
                            setCreateOverrides((prev) => ({
                              ...prev,
                              [f.key]: !prev[f.key],
                            }))
                          }
                          sx={{
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                            fontSize: '0.7rem',
                            minWidth: 0,
                            px: 0.5,
                            color: 'text.secondary',
                          }}
                        >
                          {overridden ? t('attendance:approvals.useAutoComputed') : t('attendance:approvals.overrideManually')}
                        </Button>
                      )}
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={createSaving}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCreate}
            disabled={!canSaveCreate || createSaving}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {createSaving ? <CircularProgress size={18} /> : t('attendance:approvals.recordRequest')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail, and — on a pending row — the place to decide it.
          It answers "what am I deciding?" and "what was decided?", and since it holds
          the justification it is also where the decision belongs. Both entry points
          share `handleSingleAction`, so there is one implementation. */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ py: 2, fontSize: '1rem', fontWeight: 600 }}>
          {t('attendance:approvals.detailTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {detailRow && (
            <Stack spacing={1.5}>
              <DetailRow
                label={t('attendance:approvals.colWorker')}
                value={detailRow.worker?.name ?? '—'}
              />
              <DetailRow
                label={t('attendance:approvals.colType')}
                value={typeLabel(detailRow.type, t)}
              />
              <DetailRow
                label={t('attendance:approvals.colStatus')}
                value={statusLabel(detailRow.status, t)}
              />
              <DetailRow
                label={t('attendance:approvals.colSubmitted')}
                value={formatDateTime(detailRow.submitted_at ?? detailRow.created_at, i18n.language)}
              />
              {/* Every field the worker filled in, including `reason` — the one thing
                  that justifies a decision. Rendered from the payload rather than a
                  fixed list because each request type carries its own shape, but in a
                  FIXED order (`PAYLOAD_FIELD_ORDER`): `Object.entries` gave whatever
                  order the JSON happened to have, which is how "End date" came to sit
                  above "Start date".

                  Id-valued keys are resolved to a name or dropped — see
                  `visiblePayloadEntries`. */}
              {visiblePayloadEntries(detailRow, leaveTypeNameById).map(([key, value]) => (
                <DetailRow
                  key={key}
                  label={payloadFieldLabel(key, t)}
                  value={formatPayloadValue(key, value, t)}
                />
              ))}
              {detailRow.status !== 'pending' && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <DetailRow
                    label={t('attendance:approvals.detailDecidedBy')}
                    value={
                      workerNameById.get(detailRow.reviewed_by ?? '')
                      ?? t('attendance:approvals.detailDecidedByUnknown')
                    }
                  />
                  <DetailRow
                    label={t('attendance:approvals.detailDecidedAt')}
                    value={formatDateTime(detailRow.reviewed_at, i18n.language)}
                  />
                  {(detailRow.approval_chain ?? [])
                    .filter((step) => typeof step?.note === 'string' && step.note)
                    .map((step, index) => (
                      <DetailRow
                        key={`note-${index}`}
                        label={t('attendance:approvals.detailDecisionNote')}
                        value={String(step.note)}
                      />
                    ))}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => setDetailRow(null)}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('common:actions.close')}
          </Button>
          {/* Decide from HERE, on a pending row.
              This dialog holds the `reason` the worker typed — the only thing that
              justifies a decision. Without these buttons an admin had to read the
              justification, close, find the row again and then act, and the earlier
              version of this comment defended that as avoiding "two homes for one
              action". That argument was wrong: the concern behind it is two
              IMPLEMENTATIONS, and both entry points call the same
              `handleSingleAction`. A list and a detail view each offering the primary
              action is ordinary. */}
          {detailRow?.status === 'pending' && (
            <>
              <Button
                variant="outlined"
                color="error"
                disabled={deciding}
                onClick={() => decideFromDetail('rejected')}
                sx={{ borderRadius: 1, textTransform: 'none' }}
              >
                {t('attendance:approvals.reject')}
              </Button>
              <Button
                variant="contained"
                disabled={deciding}
                onClick={() => decideFromDetail('approved')}
                sx={{ borderRadius: 1, textTransform: 'none' }}
              >
                {deciding ? <CircularProgress size={18} /> : t('attendance:approvals.approve')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApprovalList;
